import { useCallback, useEffect, useMemo, useState } from "react";
import { Edit3, Plus, Save, ShieldCheck, Trash2, X } from "lucide-react";
import { api, apiErrToStr } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TYPE_OPTIONS = [
  { value: "ramais", label: "Entre ramais" },
  { value: "local", label: "Local" },
  { value: "ddd", label: "DDD / nacional" },
  { value: "ddi", label: "DDI / internacional" },
  { value: "personalizada", label: "Personalizada" },
];

const TYPE_PATTERNS = {
  ramais: "^[0-9]{3,6}$",
  local: "^0[2-9][0-9]{7,8}$",
  ddd: "^0[1-9][0-9]{9,10}$",
  ddi: "^00[1-9][0-9]{7,14}$",
  personalizada: "0*",
};

const PERMISSION_TEMPLATES = [
  {
    id: "local_fixed_1_5",
    nome: "Local fixo 1 a 5",
    descricao: "Permite servicos publicos discando 0 + 1xx, chamadas locais fixas iniciadas de 1 a 5 e bloqueia os demais destinos.",
    regras: [
      { nome: "Servicos publicos 0 + 1xx", tipo: "personalizada", padrao: "^01[0-9]{2}$", acao: "allow", prioridade: 10, routeKey: "service" },
      { nome: "Local fixo 1 a 5", tipo: "local", padrao: "^0[1-5][0-9]{7}$", acao: "allow", prioridade: 100 },
      { nome: "Bloquear demais destinos", tipo: "personalizada", padrao: "^.+$", acao: "deny", prioridade: 900 },
    ],
  },
  {
    id: "local_fixed_mobile_1_9",
    nome: "Local fixo e celular 1 a 9",
    descricao: "Permite servicos publicos discando 0 + 1xx, chamadas locais fixas e celulares iniciadas de 1 a 9 e bloqueia os demais destinos.",
    regras: [
      { nome: "Servicos publicos 0 + 1xx", tipo: "personalizada", padrao: "^01[0-9]{2}$", acao: "allow", prioridade: 10, routeKey: "service" },
      { nome: "Local fixo/celular 1 a 9", tipo: "local", padrao: "^0[1-9][0-9]{7,8}$", acao: "allow", prioridade: 100 },
      { nome: "Bloquear demais destinos", tipo: "personalizada", padrao: "^.+$", acao: "deny", prioridade: 900 },
    ],
  },
  {
    id: "ddd_national_fixed_mobile",
    nome: "DDD nacional fixo e celular",
    descricao: "Permite servicos publicos discando 0 + 1xx, chamadas locais e DDD nacional fixo/celular e bloqueia os demais destinos.",
    regras: [
      { nome: "Servicos publicos 0 + 1xx", tipo: "personalizada", padrao: "^01[0-9]{2}$", acao: "allow", prioridade: 10, routeKey: "service" },
      { nome: "Local fixo/celular", tipo: "local", padrao: "^0[1-9][0-9]{7,8}$", acao: "allow", prioridade: 100 },
      { nome: "DDD nacional fixo/celular", tipo: "ddd", padrao: "^00?[1-9][0-9][1-9][0-9]{7,8}$", acao: "allow", prioridade: 110 },
      { nome: "Bloquear demais destinos", tipo: "personalizada", padrao: "^.+$", acao: "deny", prioridade: 900 },
    ],
  },
];

const newId = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

function maskToRegex(value) {
  const raw = String(value || "").trim();
  if (!raw) return raw;
  if (raw.startsWith("^") || raw.endsWith("$") || raw.includes("[")) return raw;
  const escaped = raw.replace(/[.+?^${}()|[\]\\]/g, "\\$&");
  let out = "";
  for (const char of escaped) {
    if (char === "x" || char === "X") out += "[0-9]";
    else if (char === "*") out += ".*";
    else out += char;
  }
  return `^${out}$`.replace(/(?:\[0-9\]){2,}/g, (match) => `[0-9]{${match.length / 5}}`);
}

function regexToMask(value) {
  let raw = String(value || "");
  if (raw.includes("[") || raw.includes("\\") || raw.includes(".") || raw.includes("(") || raw.includes("|")) return raw;
  const simple = raw.match(/^\^([0-9]*)(?:\[0-9\]|\[0-9\]\{(\d+)\})\$$/);
  if (simple) return simple[1] + "x".repeat(simple[2] ? Number(simple[2]) : 1);
  raw = raw.replace(/^\^/, "").replace(/\$$/, "");
  raw = raw.replace(/\[0-9\]\{(\d+)\}/g, (_, count) => "x".repeat(Number(count)));
  return raw.replace(/\[0-9\]/g, "x").replace(/\.\*/g, "*");
}

function emptyRule(tipo = "ramais") {
  return {
    id: newId(), nome: "", tipo, padrao: TYPE_PATTERNS[tipo], acao: "allow",
    rota_id: "", prioridade: 100, enabled: true,
  };
}

export default function CallPermissions() {
  const { user, activeTenantId } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const [classes, setClasses] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ tenant_id: user?.tenant_id || "", nome: "", descricao: "", regras: [emptyRule()], enabled: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const unscoped = isSuperAdmin ? { skipTenantScope: true } : undefined;
      const [permissionRows, routeRows, extensionRows, tenantRows] = await Promise.all([
        api.get("/call-permissions", unscoped), api.get("/rotas", unscoped), api.get("/ramais", unscoped),
        isSuperAdmin ? api.get("/tenants", { skipTenantScope: true }) : Promise.resolve({ data: [] }),
      ]);
      setClasses(permissionRows.data);
      setRoutes(routeRows.data);
      setExtensions(extensionRows.data);
      setTenants(tenantRows.data);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  const tenantMap = useMemo(() => Object.fromEntries(tenants.map((tenant) => [tenant.id, tenant.nome])), [tenants]);
  const routeMap = useMemo(() => Object.fromEntries(routes.map((route) => [route.id, route])), [routes]);
  const scopedClasses = useMemo(() => {
    if (!isSuperAdmin || !activeTenantId) return classes;
    return classes.filter((row) => String(row?.tenant_id || "") === String(activeTenantId));
  }, [activeTenantId, classes, isSuperAdmin]);
  const scopedExtensions = useMemo(() => {
    if (!isSuperAdmin || !activeTenantId) return extensions;
    return extensions.filter((row) => String(row?.tenant_id || "") === String(activeTenantId));
  }, [activeTenantId, extensions, isSuperAdmin]);
  const scopedRoutes = useMemo(() => {
    if (!isSuperAdmin || !activeTenantId) return routes;
    return routes.filter((row) => String(row?.tenant_id || "") === String(activeTenantId));
  }, [activeTenantId, isSuperAdmin, routes]);
  const assignedCount = (permissionId) => scopedExtensions.filter((extension) => extension.permission_class_id === permissionId).length;
  const availableRoutes = scopedRoutes.filter((route) => route.tenant_id === form.tenant_id && route.enabled !== false);

  const openForm = (row = null) => {
    setError("");
    setEditing(row);
    setForm(row ? {
      ...row,
      regras: (row.regras || []).map((rule) => ({ ...rule, padrao: regexToMask(rule.padrao), rota_id: rule.rota_id || "" })),
    } : { tenant_id: user?.tenant_id || "", nome: "", descricao: "", regras: [emptyRule()], enabled: true });
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditing(null); };
  const setRule = (index, values) => setForm((current) => ({
    ...current,
    regras: current.regras.map((rule, ruleIndex) => ruleIndex === index ? { ...rule, ...values } : rule),
  }));
  const removeRule = (index) => setForm((current) => ({ ...current, regras: current.regras.filter((_, ruleIndex) => ruleIndex !== index) }));
  const applyTemplate = (template) => {
    const defaultRouteId = availableRoutes[0]?.id || "";
    const serviceRouteId = availableRoutes.find((route) => route.padrao === "^0(1[0-9]{2})$" || String(route.nome || "").toLowerCase().includes("1xx"))?.id || defaultRouteId;
    setForm((current) => ({
      ...current,
      nome: current.nome || template.nome,
      descricao: current.descricao || template.descricao,
      regras: template.regras.map((rule) => ({
        ...emptyRule(rule.tipo),
        ...rule,
        routeKey: undefined,
        rota_id: rule.acao === "deny" || rule.tipo === "ramais" ? "" : (rule.routeKey === "service" ? serviceRouteId : defaultRouteId),
      })),
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true); setError("");
    try {
      if (!form.regras.length) throw new Error("Cadastre ao menos uma regra.");
      const payload = {
        ...form,
        regras: form.regras.map((rule) => ({
          ...rule,
          padrao: maskToRegex(rule.padrao),
          rota_id: rule.acao === "deny" || rule.tipo === "ramais" ? null : (rule.rota_id || null),
        })),
      };
      if (editing) await api.put(`/call-permissions/${editing.id}`, payload);
      else await api.post("/call-permissions", payload);
      closeForm();
      await load();
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeClass = async (row) => {
    if (!window.confirm(`Excluir a classe ${row.nome}?`)) return;
    try {
      await api.delete(`/call-permissions/${row.id}`);
      await load();
    } catch (err) {
      window.alert(apiErrToStr(err?.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-5" data-testid="call-permissions-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Controle por ramal e rota</div>
          <h1 className="text-2xl font-semibold tracking-tight">Permissoes de ligacoes</h1>
        </div>
        <button className="sbc-btn sbc-btn-primary" onClick={() => openForm()}><Plus size={14} /> Adicionar classe</button>
      </div>

      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] px-4 py-3 text-xs text-[color:var(--text-secondary)]">
        Ramais sem classe mantem o acesso atual. Ao associar uma classe, somente regras permitidas podem completar chamadas; a regra tambem fixa a rota de saida.
      </div>

      <div className="sbc-card overflow-hidden">
        <div className="overflow-auto max-h-[70vh]">
          <table className="sbc-table">
            <thead><tr>{isSuperAdmin && <th>Cliente</th>}<th>Classe</th><th>Regras</th><th>Ramais associados</th><th>Status</th><th className="text-right">Acoes</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={isSuperAdmin ? 6 : 5} className="py-8 text-center">Carregando...</td></tr> : scopedClasses.length === 0 ? <tr><td colSpan={isSuperAdmin ? 6 : 5} className="py-8 text-center text-[color:var(--text-muted)]">Nenhuma classe cadastrada.</td></tr> : scopedClasses.map((row) => (
                <tr key={row.id}>
                  {isSuperAdmin && <td>{tenantMap[row.tenant_id] || row.tenant_id}</td>}
                  <td><div className="font-medium">{row.nome}</div><div className="text-[10px] text-[color:var(--text-muted)]">{row.descricao || "-"}</div></td>
                  <td><div className="flex max-w-xl flex-wrap gap-1">{(row.regras || []).map((rule) => <span key={rule.id} className={`sbc-tag ${rule.acao === "deny" ? "tag-offline" : "tag-online"}`}>{TYPE_OPTIONS.find((type) => type.value === rule.tipo)?.label}: {regexToMask(rule.padrao)}{rule.rota_id ? ` -> ${routeMap[rule.rota_id]?.nome || "rota"}` : ""}</span>)}</div></td>
                  <td className="font-mono">{assignedCount(row.id)}</td>
                  <td>{row.enabled ? <span className="sbc-tag tag-online">Ativa</span> : <span className="sbc-tag tag-offline">Inativa</span>}</td>
                  <td className="text-right"><div className="inline-flex gap-1"><button className="sbc-btn" title="Editar" onClick={() => openForm(row)}><Edit3 size={12} /></button><button className="sbc-btn sbc-btn-danger" title="Excluir" onClick={() => removeClass(row)}><Trash2 size={12} /></button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4" onClick={closeForm}>
        <div className="sbc-card max-h-[92vh] w-full max-w-6xl overflow-auto p-6" onClick={(event) => event.stopPropagation()}>
          <div className="mb-5 flex items-start justify-between"><div><div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{editing ? "Editar" : "Nova"}</div><h2 className="flex items-center gap-2 text-lg font-semibold"><ShieldCheck size={18} /> Classe de permissao</h2></div><button className="sbc-btn" onClick={closeForm}><X size={14} /></button></div>
          <form onSubmit={save} className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              {isSuperAdmin && <label><span className="sbc-label">Cliente</span><select className="sbc-input mt-1" value={form.tenant_id || ""} onChange={(event) => setForm({ ...form, tenant_id: event.target.value, regras: form.regras.map((rule) => ({ ...rule, rota_id: "" })) })}><option value="">-- selecione --</option>{tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.nome}</option>)}</select></label>}
              <label><span className="sbc-label">Nome da classe</span><input className="sbc-input mt-1" value={form.nome || ""} onChange={(event) => setForm({ ...form, nome: event.target.value })} placeholder="Ex: Comercial nacional" required /></label>
              <label className={isSuperAdmin ? "" : "md:col-span-2"}><span className="sbc-label">Descricao</span><input className="sbc-input mt-1" value={form.descricao || ""} onChange={(event) => setForm({ ...form, descricao: event.target.value })} placeholder="Quem deve utilizar esta classe" /></label>
            </div>

            <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-muted)] p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="sbc-label">Modelos prontos</div>
                  <p className="text-[10px] text-[color:var(--text-muted)]">Ao usar um modelo, confira a rota selecionada antes de salvar.</p>
                </div>
                {!availableRoutes.length && <span className="sbc-tag tag-offline">Cadastre uma rota ativa para usar permissoes de saida</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                {PERMISSION_TEMPLATES.map((template) => (
                  <button key={template.id} type="button" className="sbc-btn" disabled={!availableRoutes.length} onClick={() => applyTemplate(template)}>
                    {template.nome}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-[color:var(--border-default)] pt-4"><div><h3 className="text-sm font-semibold">Tabela de regras</h3><p className="text-[10px] text-[color:var(--text-muted)]">Menor prioridade e avaliada primeiro. Use x para qualquer digito, * para quantidade livre ou regex avancada iniciada por ^.</p></div><button type="button" className="sbc-btn" onClick={() => setForm({ ...form, regras: [...form.regras, emptyRule("personalizada")] })}><Plus size={13} /> Regra</button></div>

            <div className="overflow-auto border border-[color:var(--border-default)]">
              <table className="sbc-table min-w-[980px]">
                <thead><tr><th>Prioridade</th><th>Tipo</th><th>Acao</th><th>Nome</th><th>Digitos / mascara</th><th>Rota</th><th>Ativa</th><th></th></tr></thead>
                <tbody>{form.regras.map((rule, index) => <tr key={rule.id}>
                  <td><input type="number" className="sbc-input w-24 font-mono" value={rule.prioridade ?? 100} onChange={(event) => setRule(index, { prioridade: Number(event.target.value) })} /></td>
                  <td><select className="sbc-input" value={rule.tipo} onChange={(event) => { const tipo = event.target.value; setRule(index, { tipo, padrao: TYPE_PATTERNS[tipo], rota_id: tipo === "ramais" ? "" : rule.rota_id }); }}>{TYPE_OPTIONS.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</select></td>
                  <td><select className="sbc-input" value={rule.acao} onChange={(event) => setRule(index, { acao: event.target.value, rota_id: event.target.value === "deny" ? "" : rule.rota_id })}><option value="allow">Permitir</option><option value="deny">Negar</option></select></td>
                  <td><input className="sbc-input" value={rule.nome || ""} onChange={(event) => setRule(index, { nome: event.target.value })} placeholder="Ex: Celular nacional" /></td>
                  <td><input className="sbc-input min-w-48 font-mono" value={rule.padrao || ""} onChange={(event) => setRule(index, { padrao: event.target.value })} placeholder="0xxxxxxxxx" required /></td>
                  <td><select className="sbc-input min-w-56" value={rule.rota_id || ""} disabled={rule.acao === "deny" || rule.tipo === "ramais"} onChange={(event) => setRule(index, { rota_id: event.target.value })}><option value="">-- selecione --</option>{availableRoutes.map((route) => <option key={route.id} value={route.id}>{route.nome} ({regexToMask(route.padrao)})</option>)}</select></td>
                  <td><input type="checkbox" checked={rule.enabled !== false} onChange={(event) => setRule(index, { enabled: event.target.checked })} /></td>
                  <td><button type="button" className="sbc-btn sbc-btn-danger" onClick={() => removeRule(index)} title="Remover regra"><Trash2 size={12} /></button></td>
                </tr>)}</tbody>
              </table>
            </div>

            <label className="flex items-center gap-2"><input type="checkbox" checked={form.enabled !== false} onChange={(event) => setForm({ ...form, enabled: event.target.checked })} /><span className="text-sm">Classe ativada</span></label>
            {error && <div className="border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 text-xs text-[color:var(--accent-red)]">! {error}</div>}
            <div className="flex justify-end gap-2"><button type="button" className="sbc-btn" onClick={closeForm}>Cancelar</button><button type="submit" className="sbc-btn sbc-btn-primary" disabled={saving}><Save size={13} /> {saving ? "Salvando..." : "Salvar classe"}</button></div>
          </form>
        </div>
      </div>}
    </div>
  );
}
